/**
 * portal#42 - the client's answers in the Forms Hub preview (Signoff Queue → View). Shapes
 * from GET /submissions/:id/answers, plus how an answer reads on screen.
 */

export interface AnsweredField {
  fieldId: string | null;
  label: string;
  fieldType: string;
  required: boolean;
  value: string | boolean | null;
}
export interface AnsweredForm {
  formId: string | null;
  name: string;
  fields: AnsweredField[];
  /** ehr#593: the questions the client saw (the portal's snapshot), not the form as it is today. */
  asFilled?: boolean;
}
export interface SubmissionAnswers {
  hasAnswers: boolean;
  forms: AnsweredForm[];
}

// PHQ/GAD-style frequency scale (Form Builder _SC_PHQ), as in the blank preview.
export const SCALE_OPTS = [
  'Not at all',
  'Several days',
  'More than half the days',
  'Nearly every day',
];

/** A consent statement the client ticks, rather than a Yes / No question. */
export const isAcknowledgment = (f: Pick<AnsweredField, 'fieldType' | 'label'>) =>
  f.fieldType === 'checkbox' && !f.label.trim().endsWith('?');

/** How an answer reads: '' when there is none. */
export function answerText(f: Pick<AnsweredField, 'fieldType' | 'value'>): string {
  const v = f.value;
  if (v === null) return '';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  const s = v.trim();
  if (!s) return '';
  if (f.fieldType === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const d = new Date(`${s}T00:00:00Z`);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      });
    }
  }
  if (f.fieldType === 'scale' && /^[0-3]$/.test(s)) return `${s} — ${SCALE_OPTS[Number(s)]}`;
  return s;
}

/** Answered: there is an answer, and an acknowledgment counts only when ticked. */
export const isAnswered = (f: AnsweredField) =>
  isAcknowledgment(f) ? f.value === true : answerText(f) !== '';

export const answeredCount = (form: AnsweredForm) => form.fields.filter(isAnswered).length;
